import { useEffect, useState } from 'react';
import { matchPath, useLocation } from 'react-router-dom';

import { shouldDisplayHomepage } from 'utils/utils';
import { DebugDefaultRouteMap, DefaultRouteMap } from '../components/live-event/hooks/use-route-map';
import { PATHNAMES } from '../utils/admin-routing-utils';
import { useGetEvent, useIsSingleSession } from './session.hooks';

/**
 * matches a path to the current window location pathname so we can match paths outside of react-router context
 * particularly useful for matching paths in modal portals
 * @param pathToCheck - The path to look for. For example: ['/event/:eventId/en/session/:sessionId']
 * @param validations - The validations object, which is an object with the index of the path part as the key and a function that validates the part as the value.
 * @returns Boolean. True if every validation passes, false otherwise.
 */
export const useMatchRouteToWindowPath = (
	pathToCheck: string,
	validations: {
		[pathIndex: number]: (part: string) => boolean;
	},
): boolean => {

	const [isMatch, setIsMatch] = useState(false);

	const pathname = window.location.pathname;
	useEffect(() => {
		if (!pathToCheck) {
			return;
		}

		let _pathname = pathname;
		if (pathname.startsWith('/')) { // remove leading slash because it create an additional empty string in the split array
			_pathname = _pathname.slice(1);
		}
		if (pathname.endsWith('/')) { // remove trailing slash because it create an additional empty string in the split array
			_pathname = _pathname.slice(0, -1);
		}
		const splitPathname = _pathname.split('/');
		setIsMatch(splitPathname.every((part, index) => {
			return validations?.[index]?.(part);
		}));

		// only run when pathname changes or we'll get unnecessary re-renders
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pathname]);

	return isMatch;
};

const useSafePathname = (): string => {
	try {
		const location = useLocation();
		return location.pathname;
	} catch (error) {
		// don't bloat production with this error since we're going to get this a lot
		// because we have modal portals that don't live within react router context
		if (import.meta.env.VITE_DEBUG_LEVEL === '1') {
			console.error(error);
		}
		return window.location.pathname;
	}
};

export const useIsSessionsPage = (): boolean => {
	const pathname = useSafePathname();
	const isDebugSessionPath = matchPath({ path: DebugDefaultRouteMap.Sessions, end: false }, pathname) != null;
	const isPublishSessionsPath = matchPath({ path: DefaultRouteMap.Sessions, end: false }, pathname) != null;

	return isDebugSessionPath || isPublishSessionsPath;
};

export const useIsSessionPage = (): boolean => {
	const pathname = useSafePathname();
	const isEditSessionPage = useIsAdminCreateSessionPage();
	const isSingleSession = useIsSingleSessionPage();

	const isDebugSessionPage = matchPath({ path: DebugDefaultRouteMap.Session, end: false }, pathname) != null;
	const isLiveSessionPage = matchPath({ path: DefaultRouteMap.Session, end: false }, pathname) != null;

	return isEditSessionPage
		|| isDebugSessionPage
		|| isLiveSessionPage
		|| isSingleSession;
};

export const useIsAdminCreateSessionPage = (): boolean => {
	const pathname = useSafePathname();
	return matchPath({ path: PATHNAMES.Session.Session, end: false }, pathname) != null;
};

export const useIsAdminCreateSessionsPage = (): boolean => {
	const pathname = useSafePathname();

	return matchPath({ path: PATHNAMES.Event.SessionsPath, end: false }, pathname) != null;
};

export const useIsSingleSessionPage = (): boolean => {
	const pathname = useSafePathname();
	const event = useGetEvent();
	const isSingleSession = useIsSingleSession();

	if (!isSingleSession) return false;

	const isDebugSingleSessionPath = matchPath({ path: DebugDefaultRouteMap.SingleSession, end: true }, pathname) != null;
	const isAdminSingleSessionPath = matchPath({ path: DebugDefaultRouteMap.AdminSingleSession, end: true }, pathname) != null;
	const isAdminSessionPath = matchPath({ path: PATHNAMES.Session.Session, end: true }, pathname) != null;
	const isAdminEventPath = matchPath({ path: PATHNAMES.Event.Event, end: true }, pathname) != null;
	const isSingleSessionWithoutHome = !shouldDisplayHomepage(event) && matchPath({ path: DefaultRouteMap.Landing, end: true }, pathname) != null;

	return isAdminEventPath
		|| isSingleSessionWithoutHome
		|| isAdminSessionPath
		|| isDebugSingleSessionPath
		|| isAdminSingleSessionPath;
};
